import * as Icons from "lucide-react";
import { CTA, DemoTag, GlassCard, PageHeader, Shell } from "../components/site/Primitives";

const CASES = [
  {
    key: "dental",
    name: "Demo Dental Practice",
    industry: "Dental",
    before: [
      "38% of inbound calls unanswered during treatment hours",
      "Follow-up handled manually by front desk",
      "Enquiries tracked in a shared spreadsheet",
    ],
    after: [
      "Every call answered or recovered by SMS within minutes",
      "Follow-up sequences run automatically until reply",
      "One CRM record per patient enquiry with full history",
    ],
    system: ["AI Receptionist", "CRM", "Appointment Scheduling", "Automated Follow-Up", "Analytics"],
    workflow: ["Inbound call", "AI qualifies", "Availability offered", "Appointment created", "Reminder", "Review request"],
    impact: [
      "Fewer unanswered enquiries during clinical hours",
      "Consistent reminder coverage to reduce no-shows",
      "Front desk time returned to in-practice patients",
    ],
  },
  {
    key: "hvac",
    name: "Demo HVAC Company",
    industry: "HVAC",
    before: [
      "Emergency calls missed outside office hours",
      "Quotes sent with no structured follow-up",
      "No single view of job pipeline",
    ],
    after: [
      "After-hours calls captured, triaged and scheduled",
      "Quote follow-up sequence with escalation",
      "Pipeline visible by stage and value",
    ],
    system: ["AI Receptionist", "Lead Qualification", "CRM", "Workflow Automation", "Payments"],
    workflow: ["Missed call", "Recovery SMS", "Urgency triage", "Visit booked", "Quote sent", "Follow-up"],
    impact: [
      "Emergency demand no longer lost to voicemail",
      "Quotes chased on a schedule rather than ad hoc",
      "Clear operational visibility for dispatch",
    ],
  },
  {
    key: "law",
    name: "Demo Law Firm",
    industry: "Law",
    before: [
      "Intake enquiries answered inconsistently",
      "Consultations booked over long email threads",
      "No record of enquiry source",
    ],
    after: [
      "Structured intake capture with no legal advice given",
      "Consultations booked from real availability",
      "Source attribution on every enquiry",
    ],
    system: ["AI Intake Agent", "CRM", "Appointment Scheduling", "AI Reporting"],
    workflow: ["Enquiry", "Structured intake", "Screening", "Consultation booked", "Reminder", "Outcome logged"],
    impact: [
      "Faster first response on new matters",
      "Fewer scheduling round-trips",
      "Clearer view of which channels produce work",
    ],
  },
];

export default function CaseStudies() {
  return (
    <div data-testid="case-studies-page">
      <PageHeader
        testId="case-studies-title"
        eyebrow="Case studies"
        title="Demonstration scenarios that show how the system is assembled."
        sub="Elvoria AI is a new agency. Rather than present invented client results, these are worked demonstration scenarios built on the same platform."
      />

      <section className="py-20">
        <Shell>
          <div
            data-testid="case-studies-disclaimer"
            className="glass rounded-lg border-amber-300/25 bg-amber-300/[0.05] p-5"
          >
            <p className="flex items-start gap-3 text-sm text-amber-200">
              <Icons.Info className="mt-0.5 h-4 w-4 shrink-0" />
              Demonstration scenario — not a real client result. All names, figures and outcomes below are
              illustrative and describe potential impact only.
            </p>
          </div>

          <div className="mt-10 grid gap-4">
            {CASES.map((c, i) => (
              <GlassCard key={c.key} testId={`case-study-${c.key}`} className="p-6 sm:p-8">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <p className="text-[11px] uppercase tracking-[0.24em] text-elv-green">{c.industry}</p>
                    <h2 className="mt-2 text-2xl font-semibold text-elv-text">{c.name}</h2>
                  </div>
                  <DemoTag testId={`case-study-tag-${c.key}`}>Demonstration scenario</DemoTag>
                </div>

                <div className="mt-8 grid gap-6 lg:grid-cols-2">
                  <Col title="Before" items={c.before} tone="warn" testId={`case-before-${i}`} />
                  <Col title="After" items={c.after} tone="good" testId={`case-after-${i}`} />
                </div>

                <div className="mt-8 grid gap-6 lg:grid-cols-2">
                  <div data-testid={`case-system-${i}`}>
                    <p className="text-[11px] uppercase tracking-[0.2em] text-elv-dim">System</p>
                    <div className="mt-3 flex flex-wrap gap-2">
                      {c.system.map((s) => (
                        <span
                          key={s}
                          className="rounded-sm border border-white/[0.08] bg-white/[0.03] px-2.5 py-1 text-[11px] text-elv-dim"
                        >
                          {s}
                        </span>
                      ))}
                    </div>
                  </div>
                  <div data-testid={`case-workflow-${i}`}>
                    <p className="text-[11px] uppercase tracking-[0.2em] text-elv-dim">Workflow</p>
                    <div className="mt-3 flex flex-wrap items-center gap-2">
                      {c.workflow.map((w, wi, arr) => (
                        <span key={w} className="flex items-center gap-2">
                          <span className="rounded-sm border border-elv-green/20 bg-elv-green/[0.06] px-2.5 py-1 text-[11px] text-elv-text">
                            {w}
                          </span>
                          {wi < arr.length - 1 ? (
                            <Icons.ArrowRight className="h-3 w-3 text-elv-green/70" />
                          ) : null}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="mt-8 border-t border-elv-line pt-6">
                  <p className="text-[11px] uppercase tracking-[0.2em] text-elv-dim">Potential impact</p>
                  <ul className="mt-3 grid gap-2 sm:grid-cols-3">
                    {c.impact.map((im) => (
                      <li key={im} className="text-sm text-elv-dim">
                        — {im}
                      </li>
                    ))}
                  </ul>
                </div>
              </GlassCard>
            ))}
          </div>

          <div className="mt-12">
            <CTA to="/book-demo" testId="case-studies-cta">
              Book a Free AI Demo
            </CTA>
          </div>
        </Shell>
      </section>
    </div>
  );
}

const Col = ({ title, items, tone, testId }) => (
  <div data-testid={testId}>
    <p className="text-[11px] uppercase tracking-[0.2em] text-elv-dim">{title}</p>
    <ul className="mt-3 space-y-2.5">
      {items.map((i) => (
        <li key={i} className="flex items-start gap-2 text-sm text-elv-dim">
          {tone === "good" ? (
            <Icons.Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-elv-green" />
          ) : (
            <Icons.Minus className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-300/80" />
          )}
          {i}
        </li>
      ))}
    </ul>
  </div>
);
